import styled, { useTheme } from 'styled-components';
import Modal from '../../../components/Modal';
import {
  BodyMedium,
  Heading5Text,
  TitleSmall,
} from '../../../components/Texts';
import { formatUSDEnsure2Digits } from '../../../common/currency';
import Button from '../../../components/Button';
import { useDispatch, useSelector } from 'react-redux';
import { useMemo, useState, useEffect } from 'react';
import { openModal } from '../../../redux/slices/modals';
import { hapticsImpactLight } from '../../../common/haptic';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../../common/responsive';
import ReceiveCrypto from '../../../components/ReceiveCrypto';
import StripeEmbed from '../../../components/StripeEmbed';
import { useSolana } from '../../../hooks/useSolana';
import { openCenteredWindow } from '../../../utils/window';
import { useStartDepositMutation } from '../../../redux/api/authorized/deposit';
import { selectIsEurope } from '../../../redux/slices/auth';
import { useFlag } from '@unleash/proxy-client-react';
import { Capacitor } from '@capacitor/core';
import LineAndLabel from '../../../components/LineAndLabel';
import useApplePay from '../../../hooks/useApplePay';
import ApplePayButton from '../../../components/ApplePayButton';
import ApplePayMark from '../../../assets/icons/applepay.svg?react';
import {
  BRIDGE,
  COINBASE,
  EVENT_DEPOSIT_OPTION_CLICKED,
  sendEvent,
  STRIPE,
  WEB3_WALLET,
} from '../../../common/analytics';
import { selectTokensWithUSDBalance } from '../../../redux/slices/tokenSlice';
import { filterTokensByBalance } from '../../../hooks/useGiftAmountCalculations';

const PaymentOptionContainer = styled.div`
  background-color: ${({ theme }) => theme.background.secondary};
  border-radius: 16px;
  padding: 16px;
  gap: 12px;
  display: flex;
  align-items: center;
  width: 100%;
  transition: all 0.2s ease;

  ${({ disabled, theme }) =>
    !disabled &&
    `
      cursor: pointer;
      @media (hover: hover) {
        &:hover {
          background-color: ${theme.background.secondPrimary};
        }
      }
  `}
`;

const TokenInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  text-align: left;
  align-items: flex-start;
`;

const TokenLogo = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
`;

const AmountInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const TokenScroll = styled.div`
  max-height: calc(100vh - 385px);
  width: 100%;
  overflow-y: auto;
`;

const TokensSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ApplePaySection = styled.div`
  padding-bottom: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.border.primary};
`;


const PaymentSuggestionModal = ({
  onClose,
  amount,
  targetToken,
  onPaymentMethodSelected,
  onCancel,
  showDepositOptions = true,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const isEurope = useSelector(selectIsEurope);
  const { refreshTokenBalances } = useSolana();
  const { applePayEnabled, handleApplePay } = useApplePay();
  const [startDeposit] = useStartDepositMutation();

  // On web, selecting Apple Pay will show download modal
  const shouldShowApplePay = applePayEnabled || !Capacitor.isNativePlatform();
  const [coinbaseUrl, setCoinbaseUrl] = useState('');
  const bridgeOnrampEnabled = useFlag('bridgeOnrampEnabled');

  // Get fresh token balances from Redux
  const tokensWithUSDBalance = useSelector(selectTokensWithUSDBalance);

  // View state: 'selection' or 'deposit' or 'externalWallet' or 'stripe'
  const [currentView, setCurrentView] = useState('selection');

  useEffect(() => {
    if (currentView === 'deposit' && !coinbaseUrl && !isMobile) {
      (async () => {
        try {
          const onrampUrl = await startDeposit({ network: 'solana' }).unwrap();
          setCoinbaseUrl(onrampUrl);
        } catch (err) {
          console.error('Error initializing Coinbase deposit:', err);
        }
      })();
    }
  }, [currentView, isMobile, startDeposit]);

  // Simple polling: refresh balances every 3 seconds
  useEffect(() => {
    const intervalId = setInterval(() => {
      refreshTokenBalances({ force: true });
    }, 3000);

    return () => {
      clearInterval(intervalId);
    };
  }, [refreshTokenBalances]);

  // Calculate token options from fresh Redux data based on amount
  // Reuse the same filtering logic from useGiftAmountCalculations hook
  const { tokenOptions, disabledTokenOptions } = useMemo(() => {
    const { tokensWithEnoughBalance, tokensWithoutEnoughBalance } =
      filterTokensByBalance(tokensWithUSDBalance, amount);

    return {
      tokenOptions: tokensWithEnoughBalance,
      disabledTokenOptions: tokensWithoutEnoughBalance,
    };
  }, [tokensWithUSDBalance, amount]);

  const paymentOptions = useMemo(() => {
    return tokenOptions.map((token) => ({
      type: 'token',
      token,
      balance: token.balance,
      balanceUSD: token.usdValue,
    }));
  }, [tokenOptions, targetToken]);

  const disabledPaymentOptions = useMemo(() => {
    return disabledTokenOptions.map((token) => ({
      type: 'token',
      token,
      balance: token.balance,
      balanceUSD: token.usdValue,
    }));
  }, [disabledTokenOptions, targetToken]);

  const handlePaymentSelect = async (option) => {
    hapticsImpactLight();
    if (option.type === 'token') {
    onPaymentMethodSelected({
        type: 'token',
      token: option.token,
    });
    } else if (option.type === 'applePay') {
      // Handle Apple Pay directly as payment method
      onPaymentMethodSelected({
        type: 'applePay',
      });
    }
  };

  const handleDepositSelect = () => {
    hapticsImpactLight();
    // On iOS (when Apple Pay is available), skip intermediate step and go directly to external wallet
    // On Android/web, show the intermediate step to select deposit method
    if (applePayEnabled) {
      // Apple Pay is only available on iOS, so skip the intermediate step
      // Store that we came directly from selection so back button goes to selection, not deposit
      setCurrentView('externalWallet');
    } else {
      // Android or web - show intermediate step to select deposit method
      setCurrentView('deposit');
    }
  };

  const handleExternalWallet = () => {
    sendEvent(EVENT_DEPOSIT_OPTION_CLICKED, {
      provider: WEB3_WALLET,
    });
    setCurrentView('externalWallet');
  };

  const handleCoinbase = () => {
    sendEvent(EVENT_DEPOSIT_OPTION_CLICKED, {
      provider: COINBASE,
    });
    openCenteredWindow(coinbaseUrl, {}, isMobile);
    // After Coinbase, refresh balances and return to selection
    setTimeout(() => {
      refreshTokenBalances();
      setCurrentView('selection');
    }, 1000);
  };

  const handleStripe = () => {
    sendEvent(EVENT_DEPOSIT_OPTION_CLICKED, {
      provider: STRIPE,
    });
    setCurrentView('stripe');
  };

  const handleStripeSuccess = () => {
    refreshTokenBalances();
    setCurrentView('selection');
  };

  const handleBridge = () => {
    sendEvent(EVENT_DEPOSIT_OPTION_CLICKED, {
      provider: BRIDGE,
    });
    dispatch(openModal({ name: 'bridgeOnramp' }));
  };

  const handleApplePayDeposit = () => {
    handleApplePay();
    // After Apple Pay, refresh balances
    setTimeout(() => {
      refreshTokenBalances();
      setCurrentView('selection');
    }, 2000);
  };

  const handleBackToSelection = () => {
    setCurrentView('selection');
  };

  const handleBackToDeposit = () => {
    setCurrentView('deposit');
  };

  // Render deposit view
  if (currentView === 'externalWallet') {
    return (
      <Modal onClose={onClose} width={!isMobile ? '600px' : '100%'}>
        <div
          style={{
            display: 'flex',
            gap: 24,
            flexDirection: 'column',
            width: '100%',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Heading5Text
              style={{
                fontWeight: '600',
                marginTop: 20,
                color: theme.text.title,
              }}
            >
              Deposit Any Token from Your Wallet
            </Heading5Text>
          </div>
          <ReceiveCrypto onBack={handleBackToSelection} buttonText="Continue" />
        </div>
      </Modal>
    );
  }

  // Render Stripe view
  if (currentView === 'stripe') {
    return (
      <Modal onClose={onClose} width={!isMobile ? '600px' : '100%'}>
        <div
          style={{
            display: 'flex',
            gap: 24,
            flexDirection: 'column',
            width: '100%',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Heading5Text
              style={{
                fontWeight: '600',
                marginTop: 20,
                color: theme.text.title,
              }}
            >
              Deposit via Debit or Credit Card
            </Heading5Text>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          >
            <StripeEmbed amount={amount} onSuccess={handleStripeSuccess} />
            <Button
              variant='secondary'
              fullWidth={true}
              onClick={handleBackToDeposit}
              style={{ marginTop: 16 }}
            >
              Back
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  // Render deposit methods view
  if (currentView === 'deposit') {
    return (
      <Modal onClose={onClose} width={!isMobile ? '600px' : '100%'}>
        <div
          style={{
            display: 'flex',
            gap: 24,
            flexDirection: 'column',
            width: '100%',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Heading5Text
              style={{
                fontWeight: '600',
                marginTop: 20,
                color: theme.text.title,
              }}
            >
              Deposit Crypto
            </Heading5Text>
          </div>
          <TokenScroll>
            <div
              style={{
                display: 'flex',
                gap: 10,
                flexDirection: 'column',
                width: '100%',
              }}
            >
              {applePayEnabled && (
                <div style={{ marginBottom: 8 }}>
                  <ApplePayButton onClick={handleApplePayDeposit} />
                </div>
              )}
              {bridgeOnrampEnabled && (
                <Button
                  variant={Capacitor.isNativePlatform() ? 'primary' : 'secondary'}
                  onClick={handleBridge}
                  fullWidth={true}
                >
                  Bank Transfer
                </Button>
              )}
              {!Capacitor.isNativePlatform() && (
                <>
                  <LineAndLabel text='Debit/Credit card' />
                  <Button
                    disabled={!coinbaseUrl}
                    onClick={handleCoinbase}
                    fullWidth={true}
                    style={{
                      backgroundColor: `${theme.primary.accent}`,
                    }}
                  >
                    Via Coinbase
                  </Button>
                  {!isEurope && (
                    <Button
                      onClick={handleStripe}
                      fullWidth={true}
                      style={{
                        backgroundColor: `${theme.primary.purple}`,
                      }}
                    >
                      Via Stripe
                    </Button>
                  )}
                </>
              )}
              <Button
                onClick={handleExternalWallet}
                variant='secondary'
                fullWidth={true}
                style={{ marginTop: 8 }}
              >
                External Wallet
              </Button>
            </div>
          </TokenScroll>
          <div style={{ marginLeft: 'auto' }}>
            <Button variant='white' onClick={handleBackToSelection}>
              BACK
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  // Render main payment selection view
  return (
    <Modal onClose={onClose} width={!isMobile ? '600px' : '100%'}>
      <div
        style={{
          display: 'flex',
          gap: 24,
          flexDirection: 'column',
          width: '100%',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Heading5Text
            style={{
              fontWeight: '600',
              marginTop: 20,
              color: theme.text.title,
            }}
          >
            Payment Method
          </Heading5Text>
        </div>
        <TokenScroll>
          <div
            style={{
              display: 'flex',
              gap: '12px',
              flexDirection: 'column',
              width: '100%',
            }}
          >
            {/* Show Apple Pay prominently at the top - on web too (will show download modal) */}
            {shouldShowApplePay && (
              <ApplePaySection>
                <PaymentOptionContainer
                  disabled={amount && parseFloat(amount) < 5}
                  onClick={() => {
                    if (!amount || parseFloat(amount) >= 5) {
                      handlePaymentSelect({ type: 'applePay' });
                    }
                  }}
                >
                  <ApplePayMark
                    style={{
                      width: 40,
                      height: 'auto',
                      opacity: amount && parseFloat(amount) < 5 ? 0.5 : 1,
                    }}
                  />
                  <TokenInfo>
                    <TitleSmall style={{ opacity: amount && parseFloat(amount) < 5 ? 0.5 : 1 }}>
                      Apple Pay
                    </TitleSmall>
                    <BodyMedium
                      style={{
                        color: `${theme.text.secondary}`,
                        fontSize: 14,
                        opacity: amount && parseFloat(amount) < 5 ? 0.5 : 1,
                      }}
                    >
                      {applePayEnabled
                        ? 'Pay with debit card'
                        : 'Available in iOS app'}
                    </BodyMedium>
                  </TokenInfo>
                  <AmountInfo>
                    <BodyMedium
                      style={{
                        color: `${theme.text.secondary}`,
                        fontSize: 12,
                        opacity: amount && parseFloat(amount) < 5 ? 0.5 : 1,
                      }}
                    >
                      {amount && parseFloat(amount) < 5
                        ? 'Minimum $5'
                        : applePayEnabled
                          ? 'Pay directly'
                          : 'Download app'}
                    </BodyMedium>
                  </AmountInfo>
                </PaymentOptionContainer>
              </ApplePaySection>
            )}

            {/* Tokens section - includes tokens and "Add Funds" option */}
            {(paymentOptions.length > 0 || disabledPaymentOptions.length > 0 || showDepositOptions) && (
              <TokensSection>
                {/* Add Funds option - always shown at the top of tokens section */}
                {showDepositOptions && (
                  <PaymentOptionContainer onClick={handleDepositSelect}>
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: theme.background.primary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 18,
                      }}
                    >
                      ➕
                    </div>
                    <TokenInfo>
                      <TitleSmall>Deposit crypto</TitleSmall>
                      <BodyMedium
                        style={{
                          color: `${theme.text.secondary}`,
                          fontSize: 14,
                        }}
                      >
                        Add tokens to pay with
                      </BodyMedium>
                    </TokenInfo>
                  </PaymentOptionContainer>
                )}

                {/* Show tokens with sufficient balance */}
            {paymentOptions.map((option, index) => (
              <PaymentOptionContainer
                    key={`token-${index}`}
                onClick={() => handlePaymentSelect(option)}
              >
                <TokenLogo
                  src={option.token.logoURI}
                  alt={option.token.symbol}
                />
                <TokenInfo>
                  <TitleSmall>
                    {option.token.name}{' '}
                    <span
                      style={{
                        color: `${theme.text.secondary}`,
                        fontWeight: 300,
                        fontSize: 12,
                      }}
                    >
                      ({option.token.symbol})
                    </span>
                  </TitleSmall>
                </TokenInfo>
                <AmountInfo>
                  <BodyMedium style={{ fontWeight: 600 }}>
                    {formatUSDEnsure2Digits(option.balanceUSD)}
                  </BodyMedium>
                  <BodyMedium
                    style={{
                      color: `${theme.text.secondary}`,
                      fontSize: 12,
                    }}
                  >
                    Available
                  </BodyMedium>
                </AmountInfo>
              </PaymentOptionContainer>
            ))}

                {/* Show tokens without sufficient balance */}
            {disabledPaymentOptions.map((option, index) => (
              <PaymentOptionContainer key={index} disabled>
                <TokenLogo
                  src={option.token.logoURI}
                  alt={option.token.symbol}
                  style={{ opacity: 0.5 }}
                />
                <TokenInfo>
                  <TitleSmall style={{ opacity: 0.5 }}>
                    {option.token.name}{' '}
                    <span
                      style={{
                        color: `${theme.text.secondary}`,
                        fontWeight: 300,
                        fontSize: 12,
                      }}
                    >
                      ({option.token.symbol})
                    </span>
                  </TitleSmall>
                </TokenInfo>
                <AmountInfo>
                  <BodyMedium style={{ fontWeight: 600, opacity: 0.5 }}>
                    {formatUSDEnsure2Digits(option.balanceUSD)}
                  </BodyMedium>
                  <BodyMedium
                    style={{
                      color: `${theme.text.secondary}`,
                      fontSize: 12,
                    }}
                  >
                    Not enough balance
                  </BodyMedium>
                </AmountInfo>
              </PaymentOptionContainer>
            ))}
              </TokensSection>
            )}
          </div>
        </TokenScroll>
        <div style={{ marginLeft: 'auto' }}>
          <Button
            variant='white'
            onClick={() => {
              if (onCancel) {
                onCancel();
              } else {
                onClose();
              }
            }}
          >
            CANCEL
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default PaymentSuggestionModal;
